import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useEffectiveAccess } from '@/hooks/useEffectiveAccess';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
import LegalReacceptanceModal from '@/components/legal/LegalReacceptanceModal';
import { getServerVerificationStatus, syncProfileVerificationForUser } from '@/services/auth';

// Routes that are always accessible (no beta check needed)
const PUBLIC_ROUTES = [
  '/auth',
  '/reset-password',
  '/beta-status',
  '/imprint',
  '/privacy',
  '/terms',
  '/impressum',
  '/datenschutz',
  '/nutzungsbedingungen',
  '/contact',
];

interface BetaGateProps {
  children: React.ReactNode;
}

/**
 * Schicht A — Identity Gate.
 * Reads the canonical identity state from useEffectiveAccess and
 * redirects to /beta-status when the user is authenticated but does
 * not yet have full closed-beta access (suspended, rejected, pending,
 * unverified, or no active beta program). Admins bypass.
 * Public routes and unauthenticated users pass through.
 */
export default function BetaGate({ children }: BetaGateProps) {
  const { user, loading: authLoading } = useAuth();
  const {
    identityGate,
    loading: accessLoading,
    error: accessError,
    refetch,
  } = useEffectiveAccess();
  const location = useLocation();

  // Server-side verification check.
  //
  // The `identityGate` is derived from `public.profiles.verification_status`,
  // which can lag behind the canonical truth in `auth.users.email_confirmed_at`
  // (e.g. immediately after the user clicks the email confirmation link, or
  // when the DB trigger hasn't run yet). To keep the Beta-Flow consistent
  // with the actual auth state we:
  //
  //   1. Call the `verification-status` edge function once per session for
  //      authenticated users whose profile says "unverified".
  //   2. If Auth confirms the email is verified but the profile is out of
  //      sync, run `sync_profile_verification_for_user` and refetch
  //      `effective-access` so the gate can pass without a manual reload.
  //   3. If Auth says the email is NOT confirmed, stop checking and let the
  //      normal redirect to `/beta-status` happen.
  const [serverCheckDone, setServerCheckDone] = useState(false);
  const [healing, setHealing] = useState(false);
  const healInFlight = useRef(false);

  useEffect(() => {
    if (
      !serverCheckDone &&
      !healInFlight.current &&
      user &&
      !authLoading &&
      !accessLoading &&
      identityGate.status === 'unverified'
    ) {
      healInFlight.current = true;
      setHealing(true);
      (async () => {
        try {
          const snapshot = await getServerVerificationStatus();
          // Only run the sync RPC if Auth actually confirms the email.
          // Otherwise the profile's "unverified" status is the truth and
          // the normal redirect to /beta-status applies.
          if (snapshot?.is_confirmed && !snapshot.in_sync) {
            await syncProfileVerificationForUser(user.id);
          }
          // Always refetch when Auth says confirmed: even when the snapshot
          // reports `in_sync` we want to pull the freshest profile-derived
          // access payload (the cached one may pre-date the sync).
          if (snapshot?.is_confirmed) {
            await refetch();
          }
        } catch {
          // Silent fallback — if the server check itself fails we keep the
          // existing client-side decision (redirect to /beta-status).
        } finally {
          setServerCheckDone(true);
          setHealing(false);
          healInFlight.current = false;
        }
      })();
    }
  }, [user, authLoading, accessLoading, identityGate.status, serverCheckDone, refetch]);

  // Always allow public routes
  const isPublicRoute = PUBLIC_ROUTES.some(r => location.pathname.startsWith(r));
  if (isPublicRoute) return <>{children}</>;

  // Unauthenticated visitors see the public app shell (landing, etc.)
  if (!authLoading && !user) return <>{children}</>;

  // While loading the effective-access payload (or during self-heal), show spinner
  if (authLoading || accessLoading || healing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  // Full identity gate passed (or admin bypass) → render the app
  if (identityGate.hasAppAccess) {
    return (
      <>
        {children}
        <LegalReacceptanceModal />
      </>
    );
  }

  // Availability failure is NOT an access decision: when the effective-access
  // payload could not be loaded at all (backend hiccup, network error), keep
  // the authenticated user inside the app instead of gating them out.
  if (accessError) {
    return (
      <>
        {children}
        <LegalReacceptanceModal />
      </>
    );
  }

  // Otherwise redirect to the identity status page (handles suspended,
  // rejected, pending, unverified, no_beta — all from one source).
  return <Navigate to="/beta-status" replace />;
}
